import { ReactNode, useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getPurchaseOrder } from "../../services";
import {
  PURCHASE_ORDER_CHAIN_LABEL,
  PurchaseOrderDetail,
} from "../../types/purchase-order.type";
import { formatNumber } from "../../utils";
import { formatDateTimeBkk } from "../../utils/date";
import CustomerMatchPanel from "./CustomerMatchPanel";
import EventTimeline from "./EventTimeline";
import PurchaseOrderStatusChip from "./StatusChip";
import {
  branchRefText,
  customerRefText,
  formatCalendarDate,
  isReturnable,
  PO_COMPACT_MEDIA,
  PO_EMPTY_TEXT,
  returnByText,
} from "./data";

const DRAWER_WIDTH = 480;

interface DetailFieldProps {
  label: string;
  children: ReactNode;
}

function DetailField({ label, children }: DetailFieldProps) {
  return (
    <Stack sx={{ flex: "1 1 180px", minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {children}
      </Typography>
    </Stack>
  );
}

/** HO (W1): จับคู่ร้านได้จาก drawer และบอกหน้ารายการให้อ่านใหม่เมื่อมีการเปลี่ยน */
export interface PurchaseOrderDrawerHeadOffice {
  onChanged: () => void;
}

/** สถานะที่ HO จับคู่ร้านจาก drawer ได้ — ยังไม่มีร้าน หรือสาขาส่งคืน (มักเพราะร้านผิด) */
const HO_MATCHABLE_STATUSES = ["UNMATCHED_CUSTOMER", "RETURNED"];

interface DetailDrawerProps {
  /** uuid ของ PO ที่กำลังดู · null = ปิดอยู่ */
  purchaseOrderUuid: string | null;
  onClose: () => void;
  /** W1 เท่านั้น */
  headOffice?: PurchaseOrderDrawerHeadOffice;
  /** W7 เท่านั้น: ปุ่ม ส่งคืน HO (แสดงเฉพาะใบพร้อมใช้) */
  onRequestReturn?: (detail: PurchaseOrderDetail) => void;
}

/**
 * W6 รายละเอียด PO — ทั้งฝั่ง HO และฝั่งสาขา
 *
 * ทุกค่าบนจอนี้มาจาก API รวมถึงสาย ซึ่งอ่านจากสายหลักของร้านตอนแสดงผล จอนี้จึงไม่มี
 * ทางแก้สายหรือวันส่ง · action มีสองอย่าง: HO จับคู่ร้าน (บันทึก / บันทึกและส่งสาขา) ให้
 * ใบที่ยังไม่มีร้านหรือสาขาส่งคืน และสาขากด ส่งคืน HO ให้ใบพร้อมใช้
 */
export default function PurchaseOrderDetailDrawer({
  purchaseOrderUuid,
  onClose,
  headOffice,
  onRequestReturn,
}: DetailDrawerProps) {
  const [detail, setDetail] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const loadDetail = useCallback(async (uuid: string) => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getPurchaseOrder(uuid);
      setDetail(data);
    } catch {
      // 403 (PO ของสาขาอื่น) / 404 ถูกแจ้งโดย interceptor แล้ว
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!purchaseOrderUuid) {
      setDetail(null);
      return;
    }
    loadDetail(purchaseOrderUuid);
  }, [purchaseOrderUuid, loadDetail]);

  const shopText = detail ? customerRefText(detail.customer) : "";

  const showHeadOfficeMatch =
    Boolean(headOffice) &&
    detail !== null &&
    detail.branch !== null &&
    HO_MATCHABLE_STATUSES.includes(detail.status);
  const showReturn =
    Boolean(onRequestReturn) && detail !== null && isReturnable(detail);

  // หลังจับคู่ร้านใน drawer อ่านรายละเอียดใหม่ (สถานะ/ประวัติเปลี่ยน) และบอกหน้ารายการ
  const handleHeadOfficeMatched = () => {
    if (purchaseOrderUuid) loadDetail(purchaseOrderUuid);
    headOffice?.onChanged();
  };

  const handleRequestReturn = () => {
    if (detail && onRequestReturn) onRequestReturn(detail);
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(purchaseOrderUuid)}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: { xs: "100%", md: DRAWER_WIDTH }, maxWidth: "100%", p: 0 },
        },
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box sx={{ p: 3, pb: 0, [PO_COMPACT_MEDIA]: { p: 2, pb: 0 } }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            รายละเอียด PO
          </Typography>
          <Divider />
        </Box>

        <Box
          sx={{
            p: 3,
            [PO_COMPACT_MEDIA]: { p: 2 },
            overflowY: "auto",
            flex: 1,
          }}
        >
          {loading && (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          )}

          {!loading && !detail && (
            <Typography variant="body2" color="text.secondary">
              ไม่พบข้อมูลของ PO ใบนี้
            </Typography>
          )}

          {!loading && detail && (
            <Stack gap={3}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Typography variant="h6" fontWeight={700}>
                  {detail.po_number}
                </Typography>
                <PurchaseOrderStatusChip status={detail.status} />
              </Stack>

              <Stack direction="row" flexWrap="wrap" gap={2}>
                <DetailField label="เชน">
                  {PURCHASE_ORDER_CHAIN_LABEL[detail.chain]}
                </DetailField>
                <DetailField label="วันส่ง">
                  {formatCalendarDate(detail.delivery_date)}
                </DetailField>
                <DetailField label="วันที่สั่งซื้อ">
                  {formatCalendarDate(detail.order_date)}
                </DetailField>
                <DetailField label="จำนวนเงิน">
                  {formatNumber(detail.amount)}
                </DetailField>
                <DetailField label="สาขา">
                  {branchRefText(detail.branch)}
                </DetailField>
                <DetailField label="สาย (อ่านจากร้าน)">
                  {detail.route_number || PO_EMPTY_TEXT}
                </DetailField>
                <DetailField label="ร้าน">
                  {shopText || PO_EMPTY_TEXT}
                </DetailField>
                <DetailField label="สถานที่จัดส่งในไฟล์">
                  {detail.ship_to_code}
                  {detail.ship_to_name ? ` · ${detail.ship_to_name}` : ""}
                </DetailField>
              </Stack>

              {detail.return_info && (
                <Alert severity="warning">
                  <Typography variant="body2" fontWeight={700}>
                    สาขาส่งคืน: {detail.return_info.reason}
                  </Typography>
                  <Typography variant="caption">
                    {returnByText(detail.return_info) || PO_EMPTY_TEXT}
                  </Typography>
                </Alert>
              )}

              {showHeadOfficeMatch && (
                <>
                  <Divider />
                  <Stack gap={1}>
                    <Typography variant="body2" fontWeight={700}>
                      จับคู่ร้าน
                    </Typography>
                    <CustomerMatchPanel
                      purchaseOrder={detail}
                      refreshToken={0}
                      onMatched={handleHeadOfficeMatched}
                      allowPublish
                    />
                  </Stack>
                </>
              )}

              <Divider />

              <Stack gap={1}>
                <Typography variant="body2" fontWeight={700}>
                  ไฟล์นำเข้า
                </Typography>
                <Typography variant="body2">
                  {detail.import_batch.file_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  นำเข้าเมื่อ{" "}
                  {formatDateTimeBkk(detail.import_batch.created_at) ||
                    PO_EMPTY_TEXT}
                </Typography>
              </Stack>

              {detail.used_by && (
                <>
                  <Divider />
                  <Stack gap={1}>
                    <Typography variant="body2" fontWeight={700}>
                      บิลที่ใช้ PO ใบนี้
                    </Typography>
                    <Typography variant="body2">
                      {detail.used_by.doc_no}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTimeBkk(detail.used_by.used_at) ||
                        PO_EMPTY_TEXT}{" "}
                      · โดย {detail.used_by.sales_name || PO_EMPTY_TEXT}
                    </Typography>
                  </Stack>
                </>
              )}

              <Divider />

              <Stack gap={1.5}>
                <Typography variant="body2" fontWeight={700}>
                  ประวัติ
                </Typography>
                <EventTimeline events={detail.events} />
              </Stack>
            </Stack>
          )}
        </Box>

        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={1}
          sx={{
            p: 3,
            pt: 2,
            [PO_COMPACT_MEDIA]: { p: 2, pt: 1.5 },
            borderTop: (theme) => `1px solid ${theme.palette.draft.main}`,
          }}
        >
          {showReturn && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleRequestReturn}
            >
              ส่งคืน HO
            </Button>
          )}
          <Button onClick={onClose}>ปิด</Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
